import Video from 'twilio-video';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDeviceDetect } from 'hooks';
import { videoPriority } from 'const';

export default function useRoom(localTracks, onError, options, getAudioAndVideoTracks) {
  const { isMobile } = useDeviceDetect();
  const [room, setRoom] = useState(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isEnded, setIsEnded] = useState(false);
  const optionsRef = useRef(options);

  const [tokenS, setTokenS] = useState(false);
  const [roomNameS, setRoomNamedS] = useState(false);

  useEffect(() => {
    optionsRef.current = options;
  }, [options]);

  const connect = useCallback(
    async (token, roomName) => {
      setTokenS(token);
      setRoomNamedS(roomName);
      setIsConnecting(true);
      return Video.connect(token, { ...optionsRef.current, name: roomName }).then(
        (newRoom) => {
          setRoom(newRoom);
          const disconnect = () => newRoom.disconnect();
          newRoom.setMaxListeners(15);

          newRoom.once('disconnected', () => {
            setTimeout(() => setRoom(null));
            window.removeEventListener('beforeunload', disconnect);

            if (isMobile) {
              window.removeEventListener('pagehide', disconnect);
            }
          });

          // @ts-ignore
          window.twilioRoom = newRoom;
          setIsConnecting(false);
          window.addEventListener('beforeunload', disconnect);

          if (isMobile) {
            window.addEventListener('pagehide', disconnect);
          }
          getAudioAndVideoTracks().then((tracks) => {
            tracks.forEach((track) => {
              newRoom.localParticipant.publishTrack(track);
            });
          });
        },
        (error) => {
          onError(error);
          setIsConnecting(false);
        },
      );
    },
    [localTracks, onError, getAudioAndVideoTracks],
  );

  const applySettings = useCallback(async () => {
    if (room) {
      const tracks = await getAudioAndVideoTracks();

      const newAudioTrack = tracks.find((track) => track.kind === 'audio');
      const oldAudioTracks = Array.from(room.localParticipant.audioTracks.values());
      oldAudioTracks.forEach((t) => {
        if (t.track && t.track.stop) t.track.stop();
        room.localParticipant.unpublishTrack(t.track);
      });
      if (newAudioTrack) {
        await room.localParticipant.publishTrack(newAudioTrack, {
          priority: videoPriority.standard,
        });
      }

      const newVideoTrack = tracks.find((track) => track.kind === 'video');
      const oldVideoTracks = Array.from(room.localParticipant.videoTracks.values());
      oldVideoTracks.forEach((t) => {
        if (t.track && t.track.stop && t.track.name !== 'screen') {
          t.track.stop();
          room.localParticipant.unpublishTrack(t.track);
        }
      });
      // await room.localParticipant.unpublishTracks(oldVideoTracks.map((t) => t.track));
      if (newVideoTrack) {
        await room.localParticipant.publishTrack(newVideoTrack, {
          priority: videoPriority.standard, // choose among 'high', 'standard' or 'low'
        });
      }
    }
  }, [room, getAudioAndVideoTracks]);

  const disconnect = useCallback(async () => {
    setIsEnded(true);
    if (room) {
      room.localParticipant.tracks.forEach((trackPublication) => {
        if (trackPublication.track.stop) {
          trackPublication.track.stop();
        }
      });
      if (room.localParticipant.state === 'connected') {
        await room.disconnect();
        setRoom(null);
      }
    }
  }, [room]);

  useEffect(
    () => () => {
      if (room) {
        if (room.localParticipant.state === 'connected') {
          room.disconnect();
          setIsEnded(true);
          room.localParticipant.tracks.forEach((trackPublication) => {
            if (trackPublication.track.stop) {
              trackPublication.track.stop();
            }
          });
          setRoom(null);
        }
      }
    },
    [room],
  );

  const reconnect = useCallback(async () => {
    await room.disconnect();
    await connect(tokenS, roomNameS);
  }, [connect, disconnect, tokenS, roomNameS]);

  return { room, isConnecting, isEnded, connect, disconnect, reconnect, applySettings };
}
